import type { ActorKind } from './types'

/** Anything that carries display-only attribution: a Revision (§ lock) or a HistoryEntry. */
export interface Attributed {
  actorName?: string
  actorKind?: ActorKind
}

/**
 * The ONE display name of an actor — a § lock holder (lock bar + shading chip) or the author of a
 * modification (history table, toast).
 *
 * Kept in a single place so the very same server-written agent edit cannot be attributed
 * differently in two surfaces: "Sofia's agent" holding §3.2 in the lock bar and a raw "sofia.1"
 * one row below in the history is the drift this exists to prevent.
 *
 * `agentPattern` is the localised "{name}'s agent" form (i18n `sec_lock_agent`): the possessive
 * precedes the name in English and follows it in French, so it cannot be concatenated at the call
 * site. It is substituted with a FUNCTION replacer on purpose — a display name containing `$&`,
 * `$'` or `$1` would otherwise be reinterpreted by replace()'s substitution grammar.
 *
 * `fallbackName` is what a record written before `actorName` existed shows instead: the raw userId
 * for a history entry or a §-scoped lock, "Niobe" for a whole-part lock.
 */
export function actorDisplayName(
  who: Attributed,
  fallbackName: string,
  agentPattern: string,
): string {
  const name = who.actorName ?? fallbackName
  return who.actorKind === 'agent' ? agentPattern.replace('{name}', () => name) : name
}
